"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGlobalAuth = exports.SMARTBOOK_USER_TYPE = exports.AccountType = void 0;
require("whatwg-fetch");
const react_1 = require("react");
const react_cookie_1 = require("react-cookie");
const _1 = require(".");
const sessionUpdater_1 = require("./sessionUpdater");
const mutations_1 = require("./GraphQL/mutations");
const queries_1 = require("./GraphQL/queries");
const constants_1 = require("./constants");
var AccountType;
(function (AccountType) {
    AccountType["STANDARD"] = "STANDARD";
    AccountType["BUSINESS"] = "BUSINESS";
    AccountType["HOST"] = "HOST";
})(AccountType || (exports.AccountType = AccountType = {}));
var SMARTBOOK_USER_TYPE;
(function (SMARTBOOK_USER_TYPE) {
    SMARTBOOK_USER_TYPE["BOOKER"] = "BOOKER";
    SMARTBOOK_USER_TYPE["HOST"] = "HOST";
    SMARTBOOK_USER_TYPE["PROCUREMENT"] = "PROCUREMENT";
})(SMARTBOOK_USER_TYPE || (exports.SMARTBOOK_USER_TYPE = SMARTBOOK_USER_TYPE = {}));
const useGlobalAuth = () => {
    var _a, _b;
    if (_1.globalAuthContext.state === null) {
        throw new Error('You must set globalAuthContext before calling useGlobalAuth');
    }
    const { client, refreshTokenCookie, sessionTokenCookie, rootService, selfServeService, user: cachedUser } = _1.globalAuthContext.state;
    const [cookies, setCookie, removeCookie] = (0, react_cookie_1.useCookies)([refreshTokenCookie, sessionTokenCookie]);
    const [user, setUser] = (0, react_1.useState)(cachedUser);
    const [authenticated, setAuthenticated] = (0, react_1.useState)(Boolean(cookies[sessionTokenCookie]) && Boolean(cookies[refreshTokenCookie]) && Boolean(user));
    const [processing, setProccessing] = (0, react_1.useState)(null); // null means not yet processed, true means processing, false means finished processing
    const handleSession = (0, sessionUpdater_1.sessionUpdater)({
        setCookie,
        setAuthenticated,
        setUser
    });
    const refreshTokenBoolean = Boolean(cookies[refreshTokenCookie]);
    const { data: sessionData, loading: sessionLoading } = client.useQuery(queries_1.GET_SESSION_QUERY, {
        skip: !refreshTokenBoolean || Boolean(user),
        variables: { token: cookies[refreshTokenCookie] }
    });
    const [authenticateGuest, { loading: authGuestLoading, error: authenticateGuestError }] = client.useMutation(mutations_1.AUTHENTICATE_USER_MUTATION);
    const [createStandardGuestAccount, { loading: createStandardGuestAccountLoading, error: createStandardGuestAccountError }] = client.useMutation(mutations_1.CREATE_STANDARD_GUEST_ACCOUNT_MUTATION);
    const [createBusinessGuestAccount, { loading: createBusinessGuestAccountLoading, error: createBusinessGuestAccountError }] = client.useMutation(mutations_1.CREATE_BUSINESS_GUEST_ACCOUNT_MUTATION);
    const [createHostAccount, { loading: createHostAccountLoading, error: createHostAccountError }] = client.useMutation(mutations_1.CREATE_HOST_ACCOUNT_MUTATION);
    (0, react_1.useEffect)(() => {
        const session = sessionData === null || sessionData === void 0 ? void 0 : sessionData.getSession;
        if (session) {
            setProccessing(true);
            (async () => {
                var _a, _b, _c;
                // Add smart book user context to session
                // This will likely be moved to auth system at a later date
                const res = await fetch(`https://lambda.headbox.com/smart-book-user-auth?email=${encodeURIComponent(session.user.email)}`);
                const { role } = await res.json();
                const resSelfServe = ((_a = _1.globalAuthContext.state) === null || _a === void 0 ? void 0 : _a.selfServeService) &&
                    (await fetch(selfServeService, {
                        method: 'POST',
                        body: JSON.stringify({
                            query: `
              query Host {
                getHostInfo(hostID:${session.user.id}){
                  isSelfServeHost
                }
              }`
                        }),
                        headers: {
                            'Content-Type': 'application/json',
                            'x-headbox-accesstoken': session.jwt
                        }
                    }));
                const isSmartBookHost = (_c = (_b = (await (resSelfServe === null || resSelfServe === void 0 ? void 0 : resSelfServe.json()))) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.getHostInfo.isSelfServeHost;
                const smartBook = isSmartBookHost ? SMARTBOOK_USER_TYPE.HOST : SMARTBOOK_USER_TYPE[role];
                if (smartBook !== undefined) {
                    return handleSession(Object.assign(Object.assign({}, session), { user: Object.assign(Object.assign({}, session.user), { smartBook }) }));
                }
                return handleSession(session);
            })().catch(e => {
                console.error(e);
            }).finally(() => {
                setProccessing(false);
            });
        }
    }, [sessionData]);
    const login = async (input) => {
        const { data: authenticateGuestData } = await authenticateGuest({
            variables: { input }
        });
        handleSession(authenticateGuestData === null || authenticateGuestData === void 0 ? void 0 : authenticateGuestData.authenticateGuest);
    };
    const logout = () => {
        setProccessing(true);
        removeCookie(sessionTokenCookie, constants_1.COOKIE_OPTIONS);
        removeCookie(refreshTokenCookie, constants_1.COOKIE_OPTIONS);
        setAuthenticated(false);
        setUser(null);
        const logoutForm = document.createElement('form');
        logoutForm.action = `${rootService}/users/sign_out`;
        logoutForm.method = 'POST';
        const method = document.createElement('input');
        method.name = '_method';
        method.value = 'delete';
        method.type = 'hidden';
        logoutForm.appendChild(method);
        document.body.appendChild(logoutForm);
        logoutForm.submit();
    };
    const register = async (accountType, userDetails, password) => {
        switch (accountType) {
            case AccountType.BUSINESS: {
                const { data: createBusinessGuestAccounData } = await createBusinessGuestAccount({
                    variables: { input: { userDetails, password } }
                });
                handleSession(createBusinessGuestAccounData === null || createBusinessGuestAccounData === void 0 ? void 0 : createBusinessGuestAccounData.createBusinessGuestAccount);
                break;
            }
            case AccountType.HOST: {
                const { data: createHostAccounData } = await createHostAccount({
                    variables: { input: { userDetails, password } }
                });
                handleSession(createHostAccounData === null || createHostAccounData === void 0 ? void 0 : createHostAccounData.createHostAccount);
                break;
            }
            case AccountType.STANDARD: {
                const { data: createStandardGuestAccountData } = await createStandardGuestAccount({
                    variables: { input: { userDetails } }
                });
                handleSession(createStandardGuestAccountData === null || createStandardGuestAccountData === void 0 ? void 0 : createStandardGuestAccountData.createStandardGuestAccount);
                break;
            }
        }
    };
    const triageUser = async (email, country) => {
        const { data: guestTypeData } = await client.query(queries_1.GUEST_TYPE_QUERY, {
            variables: { email, country }
        });
        return guestTypeData === null || guestTypeData === void 0 ? void 0 : guestTypeData.getGuestType;
    };
    // (The lack of strict mode confuses this lint rule)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    const sessionProcessing = (Boolean(sessionData) && !user) && processing !== false;
    return {
        login,
        logout,
        register,
        triageUser,
        loading: processing ||
            sessionLoading ||
            sessionProcessing ||
            authGuestLoading ||
            createStandardGuestAccountLoading ||
            createBusinessGuestAccountLoading ||
            createHostAccountLoading,
        error: (_b = (_a = authenticateGuestError !== null && authenticateGuestError !== void 0 ? authenticateGuestError : createHostAccountError) !== null && _a !== void 0 ? _a : createStandardGuestAccountError) !== null && _b !== void 0 ? _b : createBusinessGuestAccountError,
        authenticated,
        user,
        jwt: cookies[sessionTokenCookie]
    };
};
exports.useGlobalAuth = useGlobalAuth;
